import * as THREE from "three";

/**
 * localFrameCamera — express the world camera in a splat avatar's LOCAL frame.
 *
 * WHY THIS EXISTS. A splat avatar's appearance is view-dependent neural
 * inference: the appearance decoder is fed plücker rays built from the camera
 * pose, and the training frame is the avatar's own. Both plücker paths
 * (computePluckerRays and GpuPlucker.compute) read exactly four things off the
 * camera — getWorldPosition(), quaternion, fov, aspect — and were handed the
 * raw scene camera, which silently assumes **avatar frame == world frame**.
 *
 * That assumption held only because the splats were added straight to the scene
 * at the origin. Once the avatar can be moved, rotated or scaled by the
 * transform gizmo (ENG-895), feeding the world camera would describe a viewer
 * that isn't where the decoder thinks it is: the geometry would move correctly
 * while the SHADING went quietly wrong — worst under rotation. Silent
 * corruption, no error.
 *
 * So: take the camera into the avatar root's local space first. The root's
 * inverse world matrix also divides out any avatar scale, which is exactly
 * right — the splats inside the group are still in their pre-scale training
 * frame, and `originScale` continues to handle the bundle's own cm→m factor
 * independently.
 *
 * The returned object is a duck-typed stand-in, not a real Camera: it exposes
 * the four members both plücker implementations touch, plus `position` for the
 * camera watch (cameraWatch.ts movedLocal). It is REUSED across
 * frames (no per-frame allocation — CLAUDE.md forbids it on this path), so
 * treat the result as valid only until the next update() call.
 */
export class LocalFrameCamera {
  fov = 50;
  aspect = 1;
  readonly quaternion = new THREE.Quaternion();

  private readonly _pos = new THREE.Vector3();
  private readonly _world = new THREE.Vector3();
  private readonly _inv = new THREE.Matrix4();
  private readonly _rootQuat = new THREE.Quaternion();
  private readonly _scratchPos = new THREE.Vector3();
  private readonly _scratchScale = new THREE.Vector3();

  /** Mirrors THREE.Object3D.getWorldPosition's contract (fills and returns target). */
  getWorldPosition(target: THREE.Vector3): THREE.Vector3 {
    return target.copy(this._pos);
  }

  /** Position in the root's frame — what the camera watch compares. Reused; do not keep it. */
  get position(): THREE.Vector3 {
    return this._pos;
  }

  /** Where the camera really is. Notes: ~/comments/aos-threejs-poc/localFrameCamera_worldPosition_two_frames.md */
  get worldPosition(): THREE.Vector3 {
    return this._world;
  }

  /**
   * Re-express `camera` in `root`'s local frame.
   *
   * Pass root = null (or an identity root) and this reduces to the camera's own
   * world pose, so the untransformed case is bit-identical to what the pipeline
   * did before.
   */
  update(camera: THREE.PerspectiveCamera, root: THREE.Object3D | null): this {
    this.fov = camera.fov;
    this.aspect = camera.aspect;
    camera.getWorldPosition(this._world);
    this._pos.copy(this._world);
    camera.getWorldQuaternion(this.quaternion);

    if (!root) return this;

    // matrixWorld can be a frame stale if the caller hasn't updated it; the
    // gizmo writes the store which re-renders, so R3F refreshes it before the
    // next inference tick. Decompose rather than assuming no scale.
    this._inv.copy(root.matrixWorld).invert();
    this._pos.applyMatrix4(this._inv);

    root.matrixWorld.decompose(this._scratchPos, this._rootQuat, this._scratchScale);
    // Local orientation = inverse(root) * world. Non-uniform root scale would
    // shear the basis and make "orientation" ill-defined; the splat-avatar
    // adapter only ever applies uniform scale, so the quaternion is exact.
    this.quaternion.premultiply(this._rootQuat.invert());
    return this;
  }
}
